import {
  toAvailableLanguage,
  type Language,
} from "../../../../utils/constants/locales";
import { RegionalInstanceId } from "../regional-instance-types";

/**
 * The CN instance serves Simplified Chinese only — see
 * `RegionalInstanceCapability.ChangeLocale`.
 */
const CN_LOCALE: Language = "zh-CN";

/**
 * Best available language from an `Accept-Language` header.
 *
 * Reference: https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Accept-Language
 *
 * Example — multiple types, weighted with the quality value syntax:
 * `Accept-Language: fr-CH, fr;q=0.9, en;q=0.8, de;q=0.7, *;q=0.5`
 *
 * Matched exactly, so `zh` or `zh-Hans` do not resolve to `zh-CN`. Browsers in
 * China send `zh-CN` first, and the CN instance is decided by hostname anyway,
 * so this only affects the global instance.
 */
function localeFromAcceptLanguage(
  acceptLanguage: string | null
): Language | undefined {
  for (const language of acceptLanguage?.split(",") ?? []) {
    // Drop the quality value syntax
    const langCode = language.split(";")[0]?.trim() ?? "";
    const match = toAvailableLanguage(langCode);
    if (match) return match;
  }

  return undefined;
}

/**
 * The locale a request should be served in, or `undefined` when no preference
 * could be determined (the caller then leaves the request as-is).
 *
 * The CN instance is forced to `zh-CN`. For the global instance the order is:
 *
 * 1. Cookie — an explicit choice in the language switcher
 * 2. URL — a non-default locale prefix, e.g. a shared `/zh-CN/…` link
 * 3. `Accept-Language`
 *
 * Note that `Language` excludes `ja` while `next.config.js` still routes it, so
 * a `/ja/…` URL resolves via the header instead of via its own prefix.
 */
export function resolveRequestLocale(input: {
  regionalInstanceId: RegionalInstanceId;
  /** `NextRequest.nextUrl.locale` — the default locale when unprefixed. */
  urlLocale: string;
  defaultLocale: string | undefined;
  cookieLocale: string | undefined;
  acceptLanguage: string | null;
}): Language | undefined {
  const {
    regionalInstanceId,
    urlLocale,
    defaultLocale,
    cookieLocale,
    acceptLanguage,
  } = input;

  if (regionalInstanceId === RegionalInstanceId.CN) {
    return CN_LOCALE;
  }

  const fromCookie = toAvailableLanguage(cookieLocale);
  if (fromCookie) {
    return fromCookie;
  }

  if (urlLocale !== defaultLocale) {
    const fromUrl = toAvailableLanguage(urlLocale);
    if (fromUrl) return fromUrl;
  }

  return localeFromAcceptLanguage(acceptLanguage);
}
